import React, { useState, useMemo, useCallback } from 'react';
import { scanEnvVariables } from '../../services/envScanApi';
import { applyInject } from '../../services/envInjectorApi';
import MultiSelectDropdown from '../shared/MultiSelectDropdown';

const MAIN_TABS = ['Overview', 'Per Service', 'Missing', 'Common', 'CORS Diff'];

const EnvScanner = () => {
  // ── Controls state ──────────────────────────────────────────
  const [showSecrets, setShowSecrets] = useState(false);

  // ── Available options (populated after first scan) ──────────
  const [availableTenants, setAvailableTenants] = useState([]);
  const [availableServiceTypes, setAvailableServiceTypes] = useState([]);

  // ── Selected filters ────────────────────────────────────────
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);

  // ── Scan state ──────────────────────────────────────────────
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Tab state ───────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);
  const [activeServiceTab, setActiveServiceTab] = useState(0);

  // ── Per-service filter state ────────────────────────────────
  const [serviceFilters, setServiceFilters] = useState({});
  const [missingOnlyFilters, setMissingOnlyFilters] = useState({});

  // ── CORS Diff state ───────────────────────────────────────
  const [corsEditMode, setCorsEditMode] = useState(false);
  const [corsEdits, setCorsEdits] = useState({});        // { appServiceName: Set<origin> }
  const [corsNewOrigin, setCorsNewOrigin] = useState('');
  const [corsSelectedServices, setCorsSelectedServices] = useState(new Set()); // which services to add new origin to
  const [corsPushLoading, setCorsPushLoading] = useState(false);
  const [corsPushResults, setCorsPushResults] = useState(null);
  const [corsShowPreview, setCorsShowPreview] = useState(false);

  // ── Scan handler ────────────────────────────────────────────
  const handleScan = useCallback(async () => {
    setLoading(true);
    setError(null);
    setScanResult(null);
    setActiveTab(0);
    setActiveServiceTab(0);
    setServiceFilters({});
    setMissingOnlyFilters({});

    try {
      const result = await scanEnvVariables({
        tenantFilter: selectedTenants.length > 0 ? selectedTenants : null,
        apiFilter: selectedServiceTypes.length > 0 ? selectedServiceTypes : null,
        showSecrets,
      });
      setScanResult(result);

      // Populate available options after first successful scan (unfiltered)
      if (result.tenants && result.tenants.length > 0) {
        // Only populate if we haven't yet (first scan) or if this was an unfiltered scan
        if (availableTenants.length === 0) {
          setAvailableTenants(result.tenants);
        } else {
          // Merge any newly discovered tenants
          setAvailableTenants((prev) => {
            const merged = new Set([...prev, ...result.tenants]);
            return [...merged].sort();
          });
        }
        if (availableServiceTypes.length === 0) {
          setAvailableServiceTypes(result.serviceTypes);
        } else {
          setAvailableServiceTypes((prev) => {
            const merged = new Set([...prev, ...result.serviceTypes]);
            return [...merged].sort();
          });
        }
      }
    } catch (err) {
      setError(err.message || 'An error occurred while scanning.');
    } finally {
      setLoading(false);
    }
  }, [selectedTenants, selectedServiceTypes, showSecrets, availableTenants.length, availableServiceTypes.length]);

  // ── Analysis (runs client-side) ─────────────────────────────
  const analysis = useMemo(() => {
    if (!scanResult || !scanResult.appServices || scanResult.appServices.length === 0) {
      return null;
    }

    const { appServices, tenants, serviceTypes } = scanResult;

    // Build lookup: "tenant|serviceType" -> { settingName: value }
    const settingsMap = {};
    for (const app of appServices) {
      const key = `${app.tenantName}|${app.serviceType}`;
      const map = {};
      for (const s of app.settings) {
        map[s.name] = s.value;
      }
      settingsMap[key] = map;
    }

    // Per service type analysis
    const byService = {};
    for (const st of serviceTypes) {
      const stTenants = tenants.filter((t) => settingsMap[`${t}|${st}`]);

      // All unique setting names for this service type
      const allNames = new Set();
      for (const t of stTenants) {
        const m = settingsMap[`${t}|${st}`];
        if (m) Object.keys(m).forEach((n) => allNames.add(n));
      }
      const sortedNames = [...allNames].sort();

      // Missing: settings that exist in some tenants but not all
      const missing = {};
      for (const t of stTenants) {
        const m = settingsMap[`${t}|${st}`] || {};
        const missingNames = sortedNames.filter((n) => !(n in m));
        if (missingNames.length > 0) {
          missing[t] = missingNames;
        }
      }

      // Common: settings with identical value across ALL tenants for this service type
      const common = {};
      for (const name of sortedNames) {
        const values = [];
        let allHave = true;
        for (const t of stTenants) {
          const m = settingsMap[`${t}|${st}`];
          if (m && name in m) {
            values.push(m[name]);
          } else {
            allHave = false;
            break;
          }
        }
        if (allHave && values.length > 0) {
          const unique = new Set(values);
          if (unique.size === 1) {
            common[name] = values[0];
          }
        }
      }

      byService[st] = {
        tenants: stTenants,
        settingNames: sortedNames,
        missing,
        common,
      };
    }

    return { settingsMap, byService };
  }, [scanResult]);

  // ── CORS Diff analysis ─────────────────────────────────────
  const corsData = useMemo(() => {
    if (!scanResult || !scanResult.appServices || scanResult.appServices.length === 0) {
      return null;
    }

    const byServiceType = {};

    // Group app services by serviceType
    const grouped = {};
    for (const app of scanResult.appServices) {
      if (!grouped[app.serviceType]) grouped[app.serviceType] = [];
      grouped[app.serviceType].push(app);
    }

    for (const [serviceType, apps] of Object.entries(grouped)) {
      const appCors = {}; // { appName: { origins: Set, resourceGroup, tenantName } }
      const allOrigins = new Set();

      for (const app of apps) {
        const corsSetting = app.settings.find(
          (s) => s.name.toLowerCase() === 'cors'
        );
        const origins = new Set();
        if (corsSetting && corsSetting.value) {
          for (const o of corsSetting.value.split('|')) {
            const trimmed = o.trim();
            if (trimmed) {
              origins.add(trimmed);
              allOrigins.add(trimmed);
            }
          }
        }
        appCors[app.name] = {
          origins,
          resourceGroup: app.resourceGroup,
          tenantName: app.tenantName,
        };
      }

      // Find inconsistencies: origins that exist in some but not all
      const sortedOrigins = [...allOrigins].sort();
      const appNames = apps.map((a) => a.name);
      let inconsistentCount = 0;
      for (const origin of sortedOrigins) {
        const hasIt = appNames.filter((n) => appCors[n].origins.has(origin));
        if (hasIt.length > 0 && hasIt.length < appNames.length) {
          inconsistentCount++;
        }
      }

      byServiceType[serviceType] = {
        apps: appCors,
        appNames,
        allOrigins: sortedOrigins,
        inconsistentCount,
      };
    }

    return byServiceType;
  }, [scanResult]);

  // ── CORS edit helpers ─────────────────────────────────────
  const initCorsEdits = useCallback(() => {
    if (!corsData) return;
    const edits = {};
    for (const [, data] of Object.entries(corsData)) {
      for (const [appName, info] of Object.entries(data.apps)) {
        edits[appName] = new Set(info.origins);
      }
    }
    setCorsEdits(edits);
    setCorsSelectedServices(new Set());
    setCorsNewOrigin('');
    setCorsPushResults(null);
    setCorsShowPreview(false);
  }, [corsData]);

  const getCorsChanges = useCallback(() => {
    if (!corsData || !corsEditMode) return [];
    const changes = [];
    for (const [serviceType, data] of Object.entries(corsData)) {
      for (const appName of data.appNames) {
        const original = data.apps[appName].origins;
        const edited = corsEdits[appName];
        if (!edited) continue;

        const added = [...edited].filter((o) => !original.has(o));
        const removed = [...original].filter((o) => !edited.has(o));

        if (added.length > 0 || removed.length > 0) {
          changes.push({
            appName,
            resourceGroup: data.apps[appName].resourceGroup,
            tenantName: data.apps[appName].tenantName,
            serviceType,
            added,
            removed,
            newValue: [...edited].join('|'),
          });
        }
      }
    }
    return changes;
  }, [corsData, corsEdits, corsEditMode]);

  const handleCorsApply = useCallback(async () => {
    const changes = getCorsChanges();
    if (changes.length === 0) return;

    setCorsPushLoading(true);
    setCorsPushResults(null);

    try {
      const targets = changes.map((c) => ({
        appServiceName: c.appName,
        resourceGroup: c.resourceGroup,
      }));
      const perAppValues = {};
      const overrides = {};
      for (const c of changes) {
        perAppValues[c.appName] = c.newValue;
        overrides[c.appName] = true;
      }

      const results = await applyInject({
        targets,
        settingName: 'CORS',
        settingValue: '',
        slotSetting: true,
        perAppValues,
        overrides,
      });

      setCorsPushResults(results);
    } catch (err) {
      setCorsPushResults([{
        appServiceName: 'Error',
        success: false,
        action: 3,
        message: err.message || 'Failed to apply CORS changes',
      }]);
    } finally {
      setCorsPushLoading(false);
    }
  }, [getCorsChanges]);

  // ── Render Helpers ──────────────────────────────────────────

  const hasScannedOnce = availableTenants.length > 0;

  const renderControls = () => (
    <div className="flex flex-wrap items-end gap-6 p-5 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 mb-6">
      <MultiSelectDropdown
        label="Tenant Filter"
        options={availableTenants}
        selected={selectedTenants}
        onChange={setSelectedTenants}
        disabled={loading || !hasScannedOnce}
        placeholder="Scan first to see tenants"
      />
      <MultiSelectDropdown
        label="Service Type Filter"
        options={availableServiceTypes}
        selected={selectedServiceTypes}
        onChange={setSelectedServiceTypes}
        disabled={loading || !hasScannedOnce}
        placeholder="Scan first to see service types"
      />
      <label className="flex items-center gap-2.5 text-sm text-gray-700 dark:text-gray-300 cursor-pointer py-2.5">
        <input
          type="checkbox"
          checked={showSecrets}
          onChange={(e) => setShowSecrets(e.target.checked)}
          disabled={loading}
          className="w-4 h-4 rounded"
        />
        Show Secrets
      </label>
      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-60" onClick={handleScan} disabled={loading}>
        {loading ? 'Scanning...' : hasScannedOnce ? 'Re-Scan' : 'Scan'}
      </button>
    </div>
  );

  const renderSummary = () => {
    if (!scanResult) return null;
    const totalMissing = analysis
      ? Object.values(analysis.byService).reduce(
          (sum, svc) =>
            sum +
            Object.values(svc.missing).reduce((s, arr) => s + arr.length, 0),
          0
        )
      : 0;

    return (
      <>
        <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Subscription: <strong>{scanResult.subscriptionName}</strong> | Scanned
          at: <strong>{new Date(scanResult.scanTimestamp).toLocaleString()}</strong> |
          Secrets: <strong>{showSecrets ? 'Visible' : 'Masked'}</strong>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
            <div className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">Tenants</div>
            <div className="text-2xl font-bold text-blue-600">{scanResult.tenants.length}</div>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
            <div className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">Service Types</div>
            <div className="text-2xl font-bold text-blue-600">{scanResult.serviceTypes.length}</div>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
            <div className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">App Services</div>
            <div className="text-2xl font-bold text-green-600">{scanResult.appServices.length}</div>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
            <div className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">Total Settings</div>
            <div className="text-2xl font-bold text-yellow-600">{scanResult.totalSettings}</div>
          </div>
          {totalMissing > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-xs font-semibold text-gray-500 uppercase">Missing Settings</div>
              <div className="text-2xl font-bold text-red-600">
                {totalMissing}
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  // ── Tab: Overview ───────────────────────────────────────────
  const renderOverview = () => {
    if (!scanResult || !analysis) return null;
    const { tenants, serviceTypes } = scanResult;
    const { settingsMap, byService } = analysis;

    return (
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Tenant / Service Matrix</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Shows which tenants have which services and the count of settings in
          each. Missing badges indicate settings that other tenants have but this
          one doesn&apos;t.
        </p>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left font-semibold text-gray-700 dark:text-gray-300 p-4 bg-gray-50 dark:bg-slate-950 min-w-[120px]">Tenant</th>
                {serviceTypes.map((st) => (
                  <th key={st} className="text-left font-semibold text-gray-700 dark:text-gray-300 p-4 bg-gray-50 dark:bg-slate-950 min-w-[140px] whitespace-nowrap">{st}</th>
                ))}
                <th className="text-left font-semibold text-gray-700 dark:text-gray-300 p-4 bg-gray-50 dark:bg-slate-950 min-w-[80px]">Total</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => {
                let totalForTenant = 0;
                return (
                  <tr key={tenant} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/50">
                    <td className="font-medium p-4 border-b border-gray-200 dark:border-slate-700 dark:text-gray-100">{tenant}</td>
                    {serviceTypes.map((st) => {
                      const key = `${tenant}|${st}`;
                      const settings = settingsMap[key];
                      if (!settings) {
                        return (
                          <td key={st} className="p-4 border-b border-gray-200 dark:border-slate-700 bg-red-50 dark:bg-red-950 text-red-700 font-medium">
                            NOT FOUND
                          </td>
                        );
                      }
                      const count = Object.keys(settings).length;
                      totalForTenant += count;
                      const svcAnalysis = byService[st];
                      const missingCount = svcAnalysis?.missing[tenant]?.length || 0;
                      return (
                        <td key={st} className="p-4 border-b border-gray-200 dark:border-slate-700 dark:text-gray-100">
                          <div className="flex flex-col gap-1.5">
                            <span>{count} settings</span>
                            {missingCount > 0 ? (
                              <span className="inline-flex w-fit px-2.5 py-1 rounded-md text-xs font-semibold bg-red-100 text-red-800">
                                {missingCount} missing
                              </span>
                            ) : (
                              <span className="inline-flex w-fit px-2.5 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-800">complete</span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td className="p-4 border-b border-gray-200 dark:border-slate-700 font-semibold dark:text-gray-100">
                      {totalForTenant}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Tab: Per-Service ────────────────────────────────────────
  const renderPerService = () => {
    if (!scanResult || !analysis) return null;
    const { serviceTypes } = scanResult;
    const { settingsMap, byService } = analysis;

    if (serviceTypes.length === 0) return <p>No service types found.</p>;

    const currentSt = serviceTypes[activeServiceTab] || serviceTypes[0];
    const svcAnalysis = byService[currentSt];
    if (!svcAnalysis) return null;

    const filterText = (serviceFilters[currentSt] || '').toLowerCase();
    const missingOnly = missingOnlyFilters[currentSt] || false;

    const filteredNames = svcAnalysis.settingNames.filter((name) => {
      if (filterText && !name.toLowerCase().includes(filterText)) return false;
      if (missingOnly) {
        const hasMissing = svcAnalysis.tenants.some((t) => {
          const m = settingsMap[`${t}|${currentSt}`];
          return !m || !(name in m);
        });
        if (!hasMissing) return false;
      }
      return true;
    });

    return (
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{currentSt} - Settings Comparison</h2>

        <div className="flex flex-wrap gap-2 mb-4">
          {serviceTypes.map((st, idx) => (
            <button
              key={st}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium ${idx === activeServiceTab ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'}`}
              onClick={() => setActiveServiceTab(idx)}
            >
              {st}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4 mb-4">
          <input
            type="text"
            placeholder="Filter settings..."
            value={serviceFilters[currentSt] || ''}
            onChange={(e) =>
              setServiceFilters((prev) => ({ ...prev, [currentSt]: e.target.value }))
            }
            className="min-w-[240px] px-4 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-slate-800 dark:text-gray-100"
          />
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={missingOnlyFilters[currentSt] || false}
              onChange={(e) =>
                setMissingOnlyFilters((prev) => ({
                  ...prev,
                  [currentSt]: e.target.checked,
                }))
              }
            />
            Show missing only
          </label>
        </div>

        <div className="flex gap-4 mb-4 text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-100"></div> Missing
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-100"></div> Common (same value across all)
          </div>
        </div>

        {/* Matrix table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left font-semibold text-gray-700 dark:text-gray-300 p-4 bg-gray-50 dark:bg-slate-950 min-w-[180px]">Setting Name</th>
                {svcAnalysis.tenants.map((t) => (
                  <th key={t} className="text-left font-semibold text-gray-700 dark:text-gray-300 p-4 bg-gray-50 dark:bg-slate-950 min-w-[140px]">{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredNames.map((name) => {
                const isCommon = name in svcAnalysis.common;
                return (
                  <tr key={name} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/50">
                    <td className="font-medium p-4 border-b border-gray-200 dark:border-slate-700 dark:text-gray-100">{name}</td>
                    {svcAnalysis.tenants.map((t) => {
                      const m = settingsMap[`${t}|${currentSt}`];
                      if (!m || !(name in m)) {
                        return (
                          <td key={t} className="p-4 border-b border-gray-200 dark:border-slate-700 bg-red-50 dark:bg-red-950 text-red-700 font-medium">
                            MISSING
                          </td>
                        );
                      }
                      return (
                        <td key={t} className={`p-4 border-b border-gray-200 dark:border-slate-700 dark:text-gray-100 ${isCommon ? 'bg-green-50 dark:bg-green-950' : ''}`} title={m[name]}>
                          {m[name]}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {filteredNames.length === 0 && (
                <tr>
                  <td
                    colSpan={svcAnalysis.tenants.length + 1}
                    style={{ textAlign: 'center', color: '#8b949e' }}
                  >
                    No settings match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Tab: Missing Variables ──────────────────────────────────
  const renderMissing = () => {
    if (!scanResult || !analysis) return null;
    const { serviceTypes } = scanResult;
    const { byService } = analysis;

    const totalMissing = Object.values(byService).reduce(
      (sum, svc) =>
        sum + Object.values(svc.missing).reduce((s, arr) => s + arr.length, 0),
      0
    );

    return (
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Missing Variables Summary</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Settings that exist in some tenants but are missing from others,
          grouped by service type.
        </p>

        {totalMissing === 0 && (
          <div className="p-4 bg-green-50 dark:bg-green-950 text-green-800 rounded-lg">
            All tenants have matching settings for each service type.
          </div>
        )}

        {serviceTypes.map((st) => {
          const svcAnalysis = byService[st];
          const missingEntries = Object.entries(svcAnalysis.missing);
          const missingCount = missingEntries.reduce(
            (s, [, arr]) => s + arr.length,
            0
          );

          return (
            <CollapsibleSection
              key={st}
              title={st}
              badge={
                missingCount > 0
                  ? { text: `${missingCount} missing across ${missingEntries.length} tenants`, type: 'red' }
                  : { text: 'No missing variables', type: 'green' }
              }
            >
              {missingEntries.length === 0 ? (
                <p style={{ color: '#8b949e', padding: '10px' }}>
                  All tenants have all settings for this service type.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="text-left font-semibold text-gray-700 dark:text-gray-300 p-4 bg-gray-50 dark:bg-slate-950 min-w-[120px]">Tenant</th>
                        <th className="text-left font-semibold text-gray-700 dark:text-gray-300 p-4 bg-gray-50 dark:bg-slate-950">Missing Settings</th>
                        <th className="text-left font-semibold text-gray-700 dark:text-gray-300 p-4 bg-gray-50 dark:bg-slate-950 min-w-[80px]">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {missingEntries
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([tenant, names]) => (
                          <tr key={tenant} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/50">
                            <td className="p-4 border-b border-gray-200 dark:border-slate-700 font-medium dark:text-gray-100">
                              {tenant}
                            </td>
                            <td className="p-4 border-b border-gray-200 dark:border-slate-700 dark:text-gray-100" style={{ whiteSpace: 'normal' }}>
                              {names.sort().join(', ')}
                            </td>
                            <td className="p-4 border-b border-gray-200 dark:border-slate-700">
                              <span className="inline-flex px-2.5 py-1 rounded-md text-xs font-semibold bg-red-100 text-red-800">{names.length}</span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CollapsibleSection>
          );
        })}
      </div>
    );
  };

  // ── Tab: Common Variables ───────────────────────────────────
  const renderCommon = () => {
    if (!scanResult || !analysis) return null;
    const { serviceTypes } = scanResult;
    const { byService } = analysis;

    return (
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Common Variables</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Settings that have the exact same value across ALL tenants for a given
          service type. These are likely shared/default configuration that
          doesn&apos;t vary by tenant.
        </p>

        {serviceTypes.map((st) => {
          const svcAnalysis = byService[st];
          const commonEntries = Object.entries(svcAnalysis.common).sort(
            ([a], [b]) => a.localeCompare(b)
          );

          return (
            <CollapsibleSection
              key={st}
              title={st}
              badge={{ text: `${commonEntries.length} common settings`, type: 'green' }}
            >
              {commonEntries.length === 0 ? (
                <p style={{ color: '#8b949e', padding: '10px' }}>
                  No settings are identical across all tenants for this service
                  type.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="text-left font-semibold text-gray-700 dark:text-gray-300 p-4 bg-gray-50 dark:bg-slate-950 min-w-[180px]">Setting Name</th>
                        <th className="text-left font-semibold text-gray-700 dark:text-gray-300 p-4 bg-gray-50 dark:bg-slate-950">Value (same across all tenants)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commonEntries.map(([name, value]) => (
                        <tr key={name} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/50">
                          <td className="p-4 border-b border-gray-200 dark:border-slate-700 font-medium dark:text-gray-100">
                            {name}
                          </td>
                          <td className="p-4 border-b border-gray-200 dark:border-slate-700 dark:text-gray-100" title={value}>{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CollapsibleSection>
          );
        })}
      </div>
    );
  };

  // ── Tab: CORS Diff ─────────────────────────────────────────
  const renderCorsDiff = () => {
    if (!scanResult || !corsData) return null;

    const serviceTypes = Object.keys(corsData).sort();
    const changes = getCorsChanges();

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">CORS Origin Comparison</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Compare CORS origins across tenants for each service type. Missing origins are highlighted in red.
            </p>
          </div>
          <div className="flex gap-2">
            {!corsEditMode ? (
              <button
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600"
                onClick={() => { setCorsEditMode(true); initCorsEdits(); }}
              >
                Edit Mode
              </button>
            ) : (
              <>
                <button
                  className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-slate-600"
                  onClick={() => { setCorsEditMode(false); setCorsEdits({}); setCorsPushResults(null); setCorsShowPreview(false); }}
                >
                  Cancel
                </button>
                {changes.length > 0 && !corsShowPreview && (
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                    onClick={() => setCorsShowPreview(true)}
                  >
                    Preview Changes ({changes.length})
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Edit mode: Add origin to multiple services */}
        {corsEditMode && (
          <div className="p-4 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg mb-4">
            <h3 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">Add Origin to Services</h3>
            <div className="flex gap-2 items-end flex-wrap">
              <div className="flex-1 min-w-[300px]">
                <input
                  type="text"
                  placeholder="https://example.com"
                  value={corsNewOrigin}
                  onChange={(e) => setCorsNewOrigin(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-gray-100"
                />
              </div>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-60"
                disabled={!corsNewOrigin.trim() || corsSelectedServices.size === 0}
                onClick={() => {
                  const origin = corsNewOrigin.trim();
                  if (!origin) return;
                  const url = origin.startsWith('http://') || origin.startsWith('https://') ? origin : `https://${origin}`;
                  setCorsEdits((prev) => {
                    const next = { ...prev };
                    for (const appName of corsSelectedServices) {
                      next[appName] = new Set([...(next[appName] || []), url]);
                    }
                    return next;
                  });
                  setCorsNewOrigin('');
                  setCorsShowPreview(false);
                }}
              >
                Add to Selected
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(corsData).flatMap(([, data]) =>
                data.appNames.map((appName) => (
                  <label key={appName} className="flex items-center gap-1.5 text-xs cursor-pointer bg-white dark:bg-slate-800 px-2 py-1 rounded border border-gray-200 dark:border-slate-600">
                    <input
                      type="checkbox"
                      checked={corsSelectedServices.has(appName)}
                      onChange={(e) => {
                        setCorsSelectedServices((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(appName);
                          else next.delete(appName);
                          return next;
                        });
                      }}
                      className="w-3 h-3"
                    />
                    <span className="text-gray-700 dark:text-gray-300">{corsData[Object.entries(corsData).find(([, d]) => d.appNames.includes(appName))?.[0]]?.apps[appName]?.tenantName || appName}</span>
                  </label>
                ))
              )}
              <button
                className="text-xs text-blue-600 hover:underline"
                onClick={() => {
                  const all = new Set();
                  for (const [, data] of Object.entries(corsData)) {
                    for (const n of data.appNames) all.add(n);
                  }
                  setCorsSelectedServices(all);
                }}
              >
                Select All
              </button>
              <button
                className="text-xs text-blue-600 hover:underline"
                onClick={() => setCorsSelectedServices(new Set())}
              >
                Deselect All
              </button>
            </div>
          </div>
        )}

        {/* Preview panel */}
        {corsShowPreview && changes.length > 0 && (
          <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg mb-4">
            <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-3">Changes Preview</h3>
            <div className="overflow-x-auto rounded-lg border border-blue-200 dark:border-blue-700">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left p-3 bg-blue-100 dark:bg-blue-900 font-semibold text-blue-800 dark:text-blue-200">App Service</th>
                    <th className="text-left p-3 bg-blue-100 dark:bg-blue-900 font-semibold text-blue-800 dark:text-blue-200">Tenant</th>
                    <th className="text-left p-3 bg-blue-100 dark:bg-blue-900 font-semibold text-green-700 dark:text-green-300">Added</th>
                    <th className="text-left p-3 bg-blue-100 dark:bg-blue-900 font-semibold text-red-700 dark:text-red-300">Removed</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((c) => (
                    <tr key={c.appName} className="hover:bg-blue-100/50 dark:hover:bg-blue-900/50">
                      <td className="p-3 border-b border-blue-200 dark:border-blue-700 font-medium dark:text-gray-100">{c.appName}</td>
                      <td className="p-3 border-b border-blue-200 dark:border-blue-700 dark:text-gray-100">{c.tenantName}</td>
                      <td className="p-3 border-b border-blue-200 dark:border-blue-700">
                        {c.added.length > 0 ? c.added.map((o) => (
                          <span key={o} className="inline-block px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs mr-1 mb-1">{o}</span>
                        )) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="p-3 border-b border-blue-200 dark:border-blue-700">
                        {c.removed.length > 0 ? c.removed.map((o) => (
                          <span key={o} className="inline-block px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs mr-1 mb-1">{o}</span>
                        )) : <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-slate-600"
                onClick={() => setCorsShowPreview(false)}
              >
                Back to Editing
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-60"
                disabled={corsPushLoading}
                onClick={handleCorsApply}
              >
                {corsPushLoading ? 'Applying...' : `Apply ${changes.length} Change${changes.length > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}

        {/* Push results */}
        {corsPushResults && (
          <div className="p-4 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg mb-4">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Push Results</h3>
            {corsPushResults.map((r) => (
              <div key={r.appServiceName} className={`flex items-center gap-2 py-1 text-sm ${r.success ? 'text-green-700' : 'text-red-700'}`}>
                <span>{r.success ? '\u2705' : '\u274C'}</span>
                <span className="font-medium">{r.appServiceName}</span>
                <span className="text-gray-500 dark:text-gray-400">— {r.message}</span>
              </div>
            ))}
            <button
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              onClick={() => { setCorsEditMode(false); setCorsEdits({}); setCorsPushResults(null); setCorsShowPreview(false); handleScan(); }}
            >
              Re-scan to Verify
            </button>
          </div>
        )}

        {/* Per service type matrix */}
        {serviceTypes.map((st) => {
          const data = corsData[st];
          if (!data || data.appNames.length === 0) return null;

          return (
            <CollapsibleSection
              key={st}
              title={st}
              badge={
                data.inconsistentCount > 0
                  ? { text: `${data.inconsistentCount} inconsistent origin${data.inconsistentCount > 1 ? 's' : ''}`, type: 'red' }
                  : data.allOrigins.length > 0
                    ? { text: `${data.allOrigins.length} origins — all consistent`, type: 'green' }
                    : { text: 'No CORS configured', type: 'red' }
              }
            >
              {data.allOrigins.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 p-2">No CORS environment variable found for any tenant in this service type.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className="text-left font-semibold text-gray-700 dark:text-gray-300 p-3 bg-gray-50 dark:bg-slate-950 min-w-[250px]">Origin</th>
                        {data.appNames.map((appName) => (
                          <th key={appName} className="text-center font-semibold text-gray-700 dark:text-gray-300 p-3 bg-gray-50 dark:bg-slate-950 min-w-[100px] whitespace-nowrap">
                            {data.apps[appName].tenantName}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(corsEditMode ? [...new Set([...data.allOrigins, ...data.appNames.flatMap((n) => [...(corsEdits[n] || [])])])].sort() : data.allOrigins).map((origin) => {
                        const presentCount = data.appNames.filter((n) => {
                          const src = corsEditMode ? corsEdits[n] : data.apps[n].origins;
                          return src && src.has(origin);
                        }).length;
                        const isInconsistent = presentCount > 0 && presentCount < data.appNames.length;

                        return (
                          <tr key={origin} className={`hover:bg-gray-50/50 dark:hover:bg-slate-800/50 ${isInconsistent ? 'bg-red-50/50 dark:bg-red-950/30' : ''}`}>
                            <td className="p-3 border-b border-gray-200 dark:border-slate-700 font-mono text-xs dark:text-gray-100">
                              {origin}
                              {isInconsistent && (
                                <span className="ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-800">INCONSISTENT</span>
                              )}
                            </td>
                            {data.appNames.map((appName) => {
                              const hasOrigin = corsEditMode
                                ? corsEdits[appName]?.has(origin)
                                : data.apps[appName].origins.has(origin);

                              if (corsEditMode) {
                                return (
                                  <td key={appName} className="p-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                    <input
                                      type="checkbox"
                                      checked={!!hasOrigin}
                                      onChange={(e) => {
                                        setCorsEdits((prev) => {
                                          const next = { ...prev };
                                          next[appName] = new Set(next[appName] || []);
                                          if (e.target.checked) next[appName].add(origin);
                                          else next[appName].delete(origin);
                                          return next;
                                        });
                                        setCorsShowPreview(false);
                                      }}
                                      className="w-4 h-4"
                                    />
                                  </td>
                                );
                              }

                              return (
                                <td key={appName} className={`p-3 border-b border-gray-200 dark:border-slate-700 text-center font-bold ${hasOrigin ? 'text-green-600' : 'text-red-500 bg-red-50 dark:bg-red-950'}`}>
                                  {hasOrigin ? '\u2713' : '\u2717'}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CollapsibleSection>
          );
        })}
      </div>
    );
  };

  // ── Main render ─────────────────────────────────────────────
  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Environment Variable Scanner</h1>
      <p className="mt-2 text-gray-600 dark:text-gray-400">
        Discover and compare App Service environment variables across all
        tenants in the subscription.
      </p>

      {renderControls()}

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-950 text-red-700 rounded-lg mb-4">
          <strong>Scan Error</strong>
          {error}
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-3 p-6 text-gray-600 dark:text-gray-400">
          <div className="w-6 h-6 border-2 border-gray-300 dark:border-slate-600 border-t-blue-600 rounded-full animate-spin"></div>
          <div>Scanning App Services... This may take a minute.</div>
        </div>
      )}

      {scanResult && !loading && (
        <>
          {renderSummary()}

          {scanResult.appServices.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-slate-400">
              <h3>No App Services Found</h3>
              <p>
                No matching tenant App Services were discovered. Try adjusting
                your filters or check that the subscription has App Services with
                recognized naming patterns.
              </p>
            </div>
          ) : (
            <>
              {/* Main tabs */}
              <div className="flex gap-2 border-b border-gray-200 dark:border-slate-700 mb-4">
                {MAIN_TABS.map((tab, idx) => (
                  <button
                    key={tab}
                    className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px ${idx === activeTab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
                    onClick={() => setActiveTab(idx)}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className={activeTab === 0 ? '' : 'hidden'}>
                {renderOverview()}
              </div>
              <div className={activeTab === 1 ? '' : 'hidden'}>
                {renderPerService()}
              </div>
              <div className={activeTab === 2 ? '' : 'hidden'}>
                {renderMissing()}
              </div>
              <div className={activeTab === 3 ? '' : 'hidden'}>
                {renderCommon()}
              </div>
              <div className={activeTab === 4 ? '' : 'hidden'}>
                {renderCorsDiff()}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

// ── Collapsible Section Sub-component ─────────────────────────
const CollapsibleSection = ({ title, badge, children }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-gray-200 dark:border-slate-700 rounded-lg mb-2 overflow-hidden">
      <div
        className="flex justify-between items-center p-4 bg-white dark:bg-slate-900 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800"
        onClick={() => setOpen(!open)}
      >
        <span className="flex items-center gap-3">
          <strong className="text-gray-900 dark:text-gray-100">{title}</strong>
          {badge && (
            <span className={`inline-flex px-2.5 py-1 rounded-md text-xs font-semibold ${badge.type === 'red' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>{badge.text}</span>
          )}
        </span>
        <span className="text-gray-400 dark:text-slate-500">{open ? '\u25BC' : '\u25B6'}</span>
      </div>
      {open && (
        <div className="p-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-950">
          {children}
        </div>
      )}
    </div>
  );
};

export default EnvScanner;
